// step-setup-contacts.js
//
// Second of the two enterprise-onboarding steps, immediately after the alerts
// review — and, for the selective program only, before the questionnaire.
//
// A review of the account's default contacts — the people every alert notifies —
// with a way to invite more. Anyone added here is marked default on the backend
// and gets their own read-only login, so they can set their own alert email and
// text number instead of going through a tokenized invitation page.
//
// At least one default contact is required to continue: an alert with nobody
// attached is a sensor that watches a freezing pipe and tells no one. A
// read-only member is the exception — they can't add anyone, so requiring it of
// them would leave them with no way out of setup.

import getContacts from '../../scripts/get-contacts'
import isAccountReadOnly from '../../scripts/is-account-read-only'
import Modal from '../../scripts/modal'
import icon from '../../util/icon'
import { renderAvatar, displayNameFor } from '../../util/avatar'

export class StepSetupContacts {

  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "step_setup_contacts";
  title = "Alert Contacts";

  // Enough rows to show who is on the list without pushing the Continue button
  // off a phone screen; the rest are counted rather than drawn, since the full
  // list is a tap away on the Contacts page.
  maxVisible = 4;

  contacts = [];
  isLoading = false;
  loadFailed = false;
  isCreating = false;
  addModal = null;

  template = `
  <div class="onboarding-step" data-for="step_setup_contacts">
    <div class="onboarding-contacts-review-stage"></div>
  </div>
  `;

  onBack = () => {
    this.onboarding.goToStep('step_setup_alerts');
  }

  onActivate = () => {
    this.onboarding.showBackButton();
    this.load();
  }

  bindActions = () => {

    this.$root = $(this.onboarding.container).find('[data-for="step_setup_contacts"]');
    this.$stage = this.$root.find('.onboarding-contacts-review-stage');

    this.$root.on('click', '[data-for="contacts_review_continue"]', () => {
      if (!this.canContinue()) {
        return;
      }
      this.goToNextStep();
    });

    this.$root.on('click', '[data-for="contacts_review_add"]', () => {
      this.openAddContact();
    });

    this.$root.on('click', '[data-for="contacts_review_retry"]', () => {
      this.load();
    });

  }

  isReadOnly () {
    return (window.currentUser.level && window.currentUser.level == 0) || isAccountReadOnly();
  }

  defaultContacts () {
    return this.contacts.filter(contact => String(contact.is_default) === '1');
  }

  canContinue () {
    return this.defaultContacts().length > 0 || this.isReadOnly();
  }

  load = async () => {

    this.isLoading = true;
    this.loadFailed = false;
    this.render();

    try {
      const contacts = await getContacts();
      this.contacts = Array.isArray(contacts) ? contacts : [];
    } catch (e) {
      console.error(e);
      this.contacts = [];
      this.loadFailed = true;
    }

    this.isLoading = false;
    this.render();

  }

  escapeHtml (value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, char => {
      switch (char) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case '\'': return '&#39;';
        default: return char;
      }
    });
  }

  /**
   * How this contact can currently be reached. A contact created moments ago has
   * an email on file but hasn't accepted or consented yet, so it reads "Invite
   * sent" rather than implying alerts already flow.
   */
  renderChannels (contact) {

    const isLegacy = String(contact.legacy) === '1';
    const accepted = String(contact.invitation_accepted) === '1';
    const emailConsent = String(contact.email_consent) === '1';
    const textConsent = String(contact.text_consent) === '1';

    const chips = [];

    if (isLegacy) {

      if (contact.email) {
        chips.push(`<span class="sa-chip sa-chip--assigned">${icon('envelope')} Email</span>`);
      }

      if (contact.tel_text) {
        chips.push(`<span class="sa-chip sa-chip--assigned">${icon('comment')} Text</span>`);
      }

      return chips.join('');

    }

    if (!accepted) {
      return `<span class="sa-chip sa-chip--pending" title="They have been emailed an invitation to set up how they receive alerts">${icon('clock')} Invite sent</span>`;
    }

    if (emailConsent) {
      chips.push(`<span class="sa-chip sa-chip--assigned">${icon('envelope')} Email</span>`);
    }

    if (textConsent) {
      chips.push(`<span class="sa-chip sa-chip--assigned">${icon('comment')} Text</span>`);
    }

    if (chips.length === 0) {
      return `<span class="sa-chip sa-chip--paused" title="They accepted the invitation but turned off both email and text alerts">Not receiving alerts</span>`;
    }

    return chips.join('');

  }

  renderContactRow (contact) {

    // resolved from this person's own profile where they have set one
    const name = displayNameFor(contact);
    const email = contact.email || '';

    return `
    <div class="onboarding-contact-row">

      ${renderAvatar(contact, { size: 32, cls: 'onboarding-contact-row__avatar' })}

      <span class="onboarding-contact-row__body">
        <span class="onboarding-contact-row__name">${this.escapeHtml(name)}</span>
        <span class="onboarding-contact-row__email">${email ? this.escapeHtml(email) : 'No email on file'}</span>
      </span>

      <span class="onboarding-contact-row__channels">
        ${this.renderChannels(contact)}
      </span>

    </div>
    `;

  }

  renderCard (body, footer) {

    this.$stage.html(`
      <div class="onboarding-contacts-review">
        <div class="onboarding-contacts-review__card">
          ${body}
          ${footer}
        </div>
      </div>
    `);

  }

  render () {

    if (!this.$stage || this.$stage.length === 0) {
      return;
    }

    if (this.isLoading) {

      this.renderCard(`
        <div class="onboarding-contacts-review__loading">
          <div class="onboarding-contacts-review__loading-text">Loading your contacts...</div>
        </div>
      `, '');

      return;

    }

    if (this.loadFailed) {

      this.renderCard(`
        <div class="onboarding-contacts-review__title">We couldn't load your contacts</div>
        <p class="onboarding-contacts-review__intro">
          Your sensor is set up and monitoring. You can review who gets alerted from the Contacts page.
        </p>
      `, `
        <div class="onboarding-contacts-review__actions">
          <button type="button" class="button button--grey-outline" data-for="contacts_review_retry">
            Try Again
          </button>
          <button type="button" class="button button--medium" data-for="contacts_review_continue">
            Continue
          </button>
        </div>
      `);

      return;

    }

    const defaults = this.defaultContacts();
    const readOnly = this.isReadOnly();
    const canContinue = this.canContinue();

    const shown = defaults.slice(0, this.maxVisible);
    const hidden = defaults.length - shown.length;

    const list = defaults.length > 0
      ? `
        <div class="onboarding-contacts-review__list">
          ${shown.map(c => this.renderContactRow(c)).join('')}
        </div>
        ${hidden > 0 ? `
          <div class="onboarding-contacts-review__more">
            and ${hidden} more ${hidden === 1 ? 'contact' : 'contacts'}
          </div>
        ` : ''}
      `
      : `
        <div class="onboarding-contacts-review__empty">
          <div class="onboarding-contacts-review__empty-icon">${icon('users')}</div>
          <div class="onboarding-contacts-review__empty-title">No one is set to receive alerts</div>
          <div class="onboarding-contacts-review__empty-text">
            ${readOnly
              ? 'Ask an account admin to add an alert contact so these alerts reach someone.'
              : 'Add at least one person so your alerts can reach someone.'}
          </div>
        </div>
      `;

    this.renderCard(`
      <div class="onboarding-contacts-review__title">
        ${canContinue
          ? `${defaults.length} ${defaults.length === 1 ? 'person gets' : 'people get'} your alerts`
          : 'Choose who gets your alerts'}
      </div>

      <p class="onboarding-contacts-review__intro">
        Everyone here is notified by every alert on your account.
      </p>

      ${defaults.length === 0 ? `
        <div class="onboarding-contacts-review__note onboarding-contacts-review__note--warning">
          <span class="onboarding-contacts-review__note-icon">${icon('triangle-exclamation')}</span>
          <span>
            At least one contact should be set to receive alerts when things are in the danger zone!
            You can change contacts on the alerts page at any time.
          </span>
        </div>
      ` : ''}

      ${list}

      ${defaults.length > 0 ? `
        <div class="onboarding-contacts-review__note onboarding-contacts-review__note--info">
          <span class="onboarding-contacts-review__note-icon">${icon('info-circle')}</span>
          <span>You can change who receives alerts from the Alerts page at any time.</span>
        </div>
      ` : ''}

      ${readOnly ? '' : `
        <div class="onboarding-contacts-review__add">
          <button type="button" class="button button--grey-outline" data-for="contacts_review_add">
            ${icon('user-plus')} Add a Contact
          </button>
        </div>
      `}
    `, `
      <div class="onboarding-contacts-review__actions">
        <button
          type="button"
          class="button button--medium"
          data-for="contacts_review_continue"
          ${canContinue ? '' : 'disabled'}
        >
          Continue
        </button>
      </div>
      ${canContinue ? '' : `
        <div class="onboarding-contacts-review__requirement">
          At least one alert contact is required to continue.
        </div>
      `}
    `);

  }

  /**
   * Name + email only. The contact sets their own text number after accepting
   * the invitation — collecting a number here would be consenting on someone
   * else's behalf.
   */
  openAddContact = () => {

    if (this.isReadOnly()) {
      return;
    }

    this.addModal = new Modal({
      title: 'Add an Alert Contact',
      maxWidth: 520,
      classes: ['onboarding-add-contact-modal'],
    });

    this.addModal.setContent(`
    <form class="contact-form onboarding-add-contact" novalidate>

      <div class="contact-form__content">

        <div class="setting">
          <label class="setting__label" for="onboarding_contact_email">Their email address</label>
          <input
            id="onboarding_contact_email"
            type="email"
            name="email"
            placeholder="name@example.com"
            autocomplete="email"
            required
          />
          <div class="setting__description">
            That's all we need. We'll email them an invitation with their own login, where they set their name,
            picture, and whether they want alerts by email, text, or both.
          </div>
        </div>

      </div>

      <div class="setting-save">
        <div class="setting-save__button">
          <button type="submit" class="button button--medium">Send Invitation</button>
        </div>
        <div class="setting-save__message"></div>
      </div>

    </form>
    `);

    const container = this.addModal.getContainer();
    const form = container.querySelector('form');

    form.addEventListener('submit', e => {
      e.preventDefault();
      this.createContact(form);
    });

    this.addModal.open();

    const emailInput = form.querySelector('[name="email"]');

    if (emailInput) {
      setTimeout(() => emailInput.focus(), 0);
    }

  }

  createContact = (form) => {

    if (this.isCreating) {
      return;
    }

    const $form = $(form);
    const $message = $form.find('.setting-save__message');
    const $submit = $form.find('button[type="submit"]');

    const emailInput = form.querySelector('[name="email"]');

    const data = {
      email: String($form.find('[name="email"]').val() || '').trim(),
      // marked default by the backend on create, so they're attached to the
      // account's existing alerts in the same request
      is_default: 1,
    };

    if (!data.email || !emailInput.checkValidity()) {
      $message.addClass('is-error').text('Please enter a valid email address.');
      return;
    }

    this.isCreating = true;
    $submit.prop('disabled', true);
    $message.removeClass('is-error').text('Sending invitation...');

    window.apiRequest('post', '/contacts/create', data)
      .then(r => {

        if (!r || r.type === 'error') {
          $message.addClass('is-error').text((r && r.message) || 'Unable to add this contact');
          return;
        }

        if (window.PubSub) {
          window.PubSub.publish('contacts/updated');
        }

        // drop the cache so the reloaded list includes the new contact
        window.userContacts = null;

        return this.load().then(() => {
          if (this.addModal) {
            this.addModal.close();
            this.addModal = null;
          }
        });

      })
      .catch(err => {
        console.error(err);
        $message.addClass('is-error').text('Unable to add this contact');
      })
      .finally(() => {
        this.isCreating = false;
        $submit.prop('disabled', false);
      });

  }

  goToNextStep = () => {

    // Only the selective program answers the sprinkler placement questions;
    // every other enterprise account is done and goes to the finish step.
    this.onboarding.goToStep(
      this.onboarding.showsSelectiveQuestions()
        ? 'after_setup_selective_questions'
        : 'after_setup_name_sensor'
    );

  }

}
